<template>
  <Dialog 
    :visible="visible" 
    @update:visible="$emit('update:visible', $event)"
    :header="isEditing ? $t('user_manage.edit_group') : $t('user_manage.new_group')" 
    :modal="true" 
    class="w-full max-w-md"
  >
    <div class="space-y-4">
      <div>
        <label class="block text-sm font-medium mb-1">{{ $t('user_manage.group_name') }}</label>
        <InputText :model-value="localGroupForm.name" @update:model-value="updateGroupName" class="w-full" />
      </div>
      <div>
        <label class="block text-sm font-medium mb-1">{{ $t('user_manage.assign_users') }}</label>
        <MultiSelect 
          :model-value="localSelectedUsers" 
          @update:model-value="updateSelectedUsers"
          :options="users" 
          optionLabel="email" 
          optionValue="id" 
          :placeholder="$t('user_manage.select_users')" 
          class="w-full" 
        />
      </div>
      <div>
        <label class="block text-sm font-medium mb-1">{{ $t('user_manage.assign_responsible_user') }}</label>
        <Dropdown 
          :model-value="localResponsible" 
          @update:model-value="updateResponsible"
          :options="responsibleOptions" 
          optionLabel="email" 
          optionValue="id" 
          :placeholder="$t('user_manage.select_responsible_user')" 
          class="w-full"
          showClear
        />
      </div>
      <div class="flex items-center gap-2">
        <Checkbox 
          :model-value="localAutoReportEnabled" 
          @update:model-value="updateAutoReportEnabled"
          :binary="true"
          inputId="auto_report_group"
        />
        <label for="auto_report_group" class="text-sm cursor-pointer">{{ $t('user_manage.auto_report_enabled') }}</label>
      </div>
    </div>
    
    <template #footer>
      <div class="flex justify-end gap-2">
        <Button 
          :label="$t('common.cancel')" 
          icon="pi pi-times" 
          class="p-button-text primary-text" 
          @click="$emit('update:visible', false)" 
        />
        <Button 
          :label="$t('common.save')" 
          icon="pi pi-check" 
          @click="handleSave" 
          class="spect-btn-primary"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, watch, computed, defineProps, defineEmits } from 'vue'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import MultiSelect from 'primevue/multiselect'
import Dropdown from 'primevue/dropdown'
import Checkbox from 'primevue/checkbox'
import Button from 'primevue/button'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  groupForm: {
    type: Object,
    default: () => ({ name: '' })
  },
  isEditing: {
    type: Boolean,
    default: false
  },
  users: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:visible', 'save'])

// Local reactive copies to avoid prop mutation
const localGroupForm = ref({ ...props.groupForm })
const localSelectedUsers = ref((props.users.filter(user => props.groupForm?.users?.some(groupUserId => groupUserId === user.id))).map(user => user.id) || [])
const localResponsible = ref(props.groupForm?.responsible || null)
const localAutoReportEnabled = ref(props.groupForm?.auto_report_enabled ?? false)

// Options: users assigned to the group only
const responsibleOptions = computed(() => {
  return props.users.filter(u => localSelectedUsers.value.includes(u.id))
})

// Watch for prop changes to update local copies
watch(() => props.groupForm, (newValue) => {
  localGroupForm.value = { ...newValue }
  localSelectedUsers.value = props.users.filter(user => props.groupForm?.users?.some(groupUserId => groupUserId === user.id)).map(user => user.id)
  localResponsible.value = props.groupForm?.responsible || null
  localAutoReportEnabled.value = props.groupForm?.auto_report_enabled ?? false
}, { deep: true, immediate: true })

// Methods to update local data
const updateGroupName = (value) => {
  localGroupForm.value.name = value
}

const updateSelectedUsers = (value) => {
  localSelectedUsers.value = value
  if (localResponsible.value && !value.includes(localResponsible.value)) {
    localResponsible.value = null
  }
}

const updateResponsible = (value) => {
  localResponsible.value = value
}

const updateAutoReportEnabled = (value) => {
  localAutoReportEnabled.value = value
}

const handleSave = () => {
  emit('save', {
    groupData: localGroupForm.value,
    selectedUsers: localSelectedUsers.value,
    responsible: localResponsible.value || undefined,
    auto_report_enabled: localAutoReportEnabled.value
  })
}
</script>
<style lang="scss" scoped>
@use "../../assets/scss/variables.scss";
@use "../../assets/scss/app/common.scss";
</style>